import { Drawer } from "antd";
import cuid from "cuid";
import { Formik } from "formik";
import { get } from "lodash";
import moment from "moment";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiBuilding } from "react-icons/bi";
import { MdArrowBack, MdArrowForward } from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import {
  DATE_FORMAT,
  INITIAL_VALUES,
  ORGANIZATION_TYPES,
  USER_TYPES,
} from "utils/constants";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import validation from "./validation";

export default function CreateEnterpriseForm({ onClose, onCreated }) {
  const { user } = useContext(StoreContext);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);
  const CREATE = t("CREATE", { returnObjects: true });
  const { t: errors } = useTranslation(NAME_SPACES.ERRORS);
  const VALIDATIONS = errors("VALIDATIONS", { returnObjects: true });

  const [chooseType, setChooseType] = useState(true);
  const [updateUserAuth] = useMutation(graphql.mutations.UPDATE_USER_AUTH);

  const [signUpAuth] = useMutation(graphql.mutations.SIGN_UP_AUTH);
  const [createUser] = useMutation(graphql.mutations.CREATE_USER, {
    refetchQueries: [
      {
        query: graphql.queries.ORGANIZATIONS,
        awaitRefetchQueries: true,
        variables: {
          ...INITIAL_VALUES,
          where: {
            contracts: {
              organization: {
                id: get(user, "organization.id"),
              },
            },
          },
        },
      },
    ],
    onCompleted: () => {
      onCreated();
    },
    onError: () =>
      updateUserAuth({
        variables: {
          where: { id: user.id },
          data: {
            phone: null,
            username: null,
            email: null,
            password: null,
          },
        },
      }),
  });
  // const [createOrganization] = useMutation(
  //   graphql.mutations.CREATE_ORGANIZATION,
  //   {
  //     refetchQueries: [
  //       {
  //         query: graphql.queries.ORGANIZATIONS,
  //         awaitRefetchQueries: true,
  //         variables: {
  //           ...INITIAL_VALUES,
  //           where: {
  //             contracts: {
  //               organization: {
  //                 id: get(user, "organization.id"),
  //               },
  //             },
  //           },
  //         },
  //       },
  //     ],
  //     onCompleted: () => {
  //       onCreated();
  //     },
  //   }
  // );
  const organizationId = useMemo(() => cuid(), []);
  const initialValues = useMemo(
    () => ({
      id: organizationId,
      name: "",
      siretNumber: "",
      type: ORGANIZATION_TYPES.OTHER,
      config: get(user, "organization.config", {}),
      broker: {
        id: organizationId,
      },
      user: {
        id: cuid(),
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        meta: { type: USER_TYPES.HUMAN_RESOURCE.type },
      },
      contracts: [
        {
          id: cuid(),
          identifier: "",
          issuanceDate: "",
          subjects: {
            commercialReferent: "",
            internalNotes: "",
          },
          organization: {
            id: get(user, "organization.id"),
          },
          counterpartyOrganization: {
            id: organizationId,
          },
        },
      ],
    }),
    [organizationId, user],
  );

  function handleTypeSelection(type, setValues) {
    setValues({
      ...initialValues,
      type,
    });
    setChooseType(false);
  }

  async function handleSubmit(values, { setSubmitting }) {
    setSubmitting(true);
    const { user, ...organization } = values;
    try {
      await signUpAuth({
        variables: {
          data: {
            id: user.id,
            username: user.email,
            email: user.email,
            phone: user.phone,
            password: cuid(),
            meta: user.meta,
            organization: { id: organization.id, name: organization.name },
          },
        },
        onCompleted: () => {
          createUser({
            variables: {
              data: {
                ...user,
                organization,
              },
            },
          });
        },
      });
    } catch (error) {
      console.error("Error creating organization:", error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validation(VALIDATIONS)}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {(formikProps) => {
        const { values, handleSubmit, isSubmitting, setValues } = formikProps;
        const formProps = { values, ...formikProps };
        const isPrivate = values.type === ORGANIZATION_TYPES.OTHER;

        return (
          <Drawer
            open={true}
            onClose={onClose}
            title={CREATE.MODAL.TITLE}
            width="600"
          >
            {chooseType ? (
              <div>
                <div className="company__mb-8">
                  <h4 className="company__text-base company__font-semibold company__text-slate-900 company__mb-2">
                    {CREATE.TYPE_SELECTION.TITLE}
                  </h4>
                  <p className="company__text-sm company__text-slate-600">
                    {CREATE.TYPE_SELECTION.DESCRIPTION}
                  </p>
                </div>

                <div className="company__space-y-4">
                  <div
                    onClick={() =>
                      handleTypeSelection(ORGANIZATION_TYPES.OTHER, setValues)
                    }
                    className="company__w-full company__p-6 company__border-2 company__border-slate-300 company__rounded-lg company__hover-border-blue-500 company__hover-bg-blue-50 company__transition-all company__text-left company__cursor-pointer"
                  >
                    <div className="company__flex company__items-start company__gap-4">
                      <div className="company__w-12 company__h-12 company__bg-blue-100 company__rounded-lg company__flex company__items-center company__justify-center company__group-hover-scale-110 company__transition-colors">
                        <BiBuilding className="company__w-6 company__h-6 company__text-blue-600" />
                      </div>
                      <div className="company__flex-1">
                        <h5 className="company__text-base company__font-semibold company__text-slate-900 company__mb-2">
                          {CREATE.TYPE_SELECTION.COMPANY.TITLE}
                        </h5>
                        <p className="company__text-sm company__text-slate-600">
                          {CREATE.TYPE_SELECTION.COMPANY.DESCRIPTION}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    onClick={() =>
                      handleTypeSelection(ORGANIZATION_TYPES.CDG, setValues)
                    }
                    className="company__w-full company__p-6 company__border-2 company__border-slate-300 company__rounded-lg company__hover-border-green-500 company__hover-bg-green-50 company__transition-all company__text-left company__cursor-pointer"
                  >
                    <div className="company__flex company__items-start company__gap-4">
                      <div className="company__w-12 company__h-12 company__bg-green-100 company__rounded-lg company__flex company__items-center company__justify-center company__group-hover-scale-110 company__transition-colors">
                        <BiBuilding className="company__w-6 company__h-6 company__text-green-600" />
                      </div>
                      <div className="company__flex-1">
                        <h5 className="company__text-base company__font-semibold company__text-slate-900 company__mb-2">
                          {CREATE.TYPE_SELECTION.COLLECTIVITY.TITLE}
                        </h5>
                        <p className="company__text-sm company__text-slate-600">
                          {CREATE.TYPE_SELECTION.COLLECTIVITY.DESCRIPTION}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="company__p-6 company__space-y-6">
                <div>
                  <h4 className="company__text-base company__font-medium company__text-slate-900 company__mb-4">
                    {CREATE.SECTIONS.GENERAL}
                  </h4>
                  <div className="company__space-y-4">
                    <Input
                      label={CREATE.FIELDS.NAME.LABEL}
                      placeholder={CREATE.FIELDS.NAME.PLACEHOLDER}
                      {...bindInputProps({ name: "name", ...formProps })}
                      required
                    />

                    {isPrivate && (
                      <Input
                        label={CREATE.FIELDS.SIRET.LABEL}
                        placeholder={CREATE.FIELDS.SIRET.PLACEHOLDER}
                        maxLength={14}
                        {...bindInputProps({
                          name: "siretNumber",
                          ...formProps,
                        })}
                        required
                      />
                    )}
                  </div>
                </div>
                <div className="company__border-t company__border-slate-200 company__pt-6">
                  <h4 className="company__text-base company__font-medium company__text-slate-900 company__mb-4">
                    {CREATE.SECTIONS.CONTACT}
                  </h4>
                  <div className="company__space-y-4">
                    <Input
                      label={CREATE.FIELDS.CONTACT_FIRST_NAME.LABEL}
                      placeholder={CREATE.FIELDS.CONTACT_FIRST_NAME.PLACEHOLDER}
                      {...bindInputProps({
                        name: "user.firstname",
                        ...formProps,
                      })}
                      required
                    />
                    <Input
                      label={CREATE.FIELDS.CONTACT_LAST_NAME.LABEL}
                      placeholder={CREATE.FIELDS.CONTACT_LAST_NAME.PLACEHOLDER}
                      {...bindInputProps({
                        name: "user.lastname",
                        ...formProps,
                      })}
                      required
                    />

                    <Input
                      label={CREATE.FIELDS.CONTACT_EMAIL.LABEL}
                      placeholder={CREATE.FIELDS.CONTACT_EMAIL.PLACEHOLDER}
                      {...bindInputProps({
                        name: "user.email",
                        ...formProps,
                      })}
                      required
                    />

                    <Input
                      label={CREATE.FIELDS.CONTACT_PHONE.LABEL}
                      placeholder={CREATE.FIELDS.CONTACT_PHONE.PLACEHOLDER}
                      {...bindInputProps({
                        name: "user.phone",
                        ...formProps,
                      })}
                    />
                  </div>
                </div>
                <div className="company__border-t company__border-slate-200 company__pt-6">
                  <h4 className="company__text-base company__font-medium company__text-slate-900 company__mb-4">
                    {CREATE.SECTIONS.CONTRACT}
                  </h4>
                  <div className="company__space-y-4">
                    <Input
                      label={CREATE.FIELDS.CONTRACT_NUMBER.LABEL}
                      placeholder={CREATE.FIELDS.CONTRACT_NUMBER.PLACEHOLDER}
                      {...bindInputProps({
                        name: "contracts.0.identifier",
                        ...formProps,
                      })}
                    />

                    <Input
                      type="date"
                      label={CREATE.FIELDS.CONTRACT_START_DATE.LABEL}
                      format={DATE_FORMAT}
                      disabledDate={(current) => current < moment()}
                      {...bindDateInputProps({
                        name: "contracts.0.issuanceDate",
                        ...formProps,
                      })}
                      required
                    />

                    <Input
                      label={CREATE.FIELDS.COMMERCIAL_REFERENT.LABEL}
                      placeholder={
                        CREATE.FIELDS.COMMERCIAL_REFERENT.PLACEHOLDER
                      }
                      {...bindInputProps({
                        name: "contracts.0.subjects.commercialReferent",
                        ...formProps,
                      })}
                    />

                    <Input
                      type="textarea"
                      label={CREATE.FIELDS.INTERNAL_NOTES.LABEL}
                      placeholder={CREATE.FIELDS.INTERNAL_NOTES.PLACEHOLDER}
                      rows={3}
                      {...bindInputProps({
                        name: "contracts.0.subjects.internalNotes",
                        ...formProps,
                      })}
                    />
                  </div>
                </div>

                <div className="company__flex company__gap-3 company__justify-end company__pt-6">
                  <Button
                    type="secondary--link"
                    onClick={() => setChooseType(true)}
                  >
                    <MdArrowBack /> {CREATE.MODAL.CANCEL}
                  </Button>
                  <Button
                    type="primary"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? "..." : `${CREATE.MODAL.SUBMIT} `}
                    <MdArrowForward />
                  </Button>
                </div>
              </div>
            )}
          </Drawer>
        );
      }}
    </Formik>
  );
}
